import { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, CalendarDays, User, Users, Phone } from "lucide-react";
import { toast } from "sonner";
import { SITE, ROOMS } from "@/data/site";

const initial = {
  name: "",
  phone: "",
  checkIn: "",
  checkOut: "",
  guests: "2",
  roomType: "couple",
  notes: "",
};

export default function BookingForm() {
  const [form, setForm] = useState(initial);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please add your name and phone number.");
      return;
    }
    if (!form.checkIn || !form.checkOut) {
      toast.error("Please pick check-in and check-out dates.");
      return;
    }
    const roomLabel =
      ROOMS.find((r) => r.id === form.roomType)?.name || form.roomType;
    const message = [
      `Hi Peak Adventures Malnad!`,
      ``,
      `I would like to make a booking enquiry:`,
      `• Name: ${form.name}`,
      `• Phone: ${form.phone}`,
      `• Check-in: ${form.checkIn}`,
      `• Check-out: ${form.checkOut}`,
      `• Guests: ${form.guests}`,
      `• Room: ${roomLabel}`,
      form.notes ? `• Notes: ${form.notes}` : null,
    ]
      .filter(Boolean)
      .join("\n");

    const url = `https://wa.me/${SITE.phoneE164}?text=${encodeURIComponent(
      message
    )}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Opening WhatsApp — we'll confirm shortly!");
  };

  return (
    <section
      id="booking"
      className="relative bg-[var(--pam-bg)]"
      style={{ padding: "5% 0" }}
      data-testid="booking-section"
    >
      <div className="container-pam grid md:grid-cols-12 gap-12 items-start">
        <div className="md:col-span-5">
          <p className="overline gold-text">Book your stay</p>
          <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[var(--pam-forest)]">
            Send us a note.
            <br />
            <span className="italic text-[var(--pam-coffee)]">
              We reply on WhatsApp.
            </span>
          </h2>
          <p className="mt-6 text-[var(--pam-muted)] font-light leading-relaxed max-w-md">
            Tell us the dates, who&apos;s coming and which stay you fancy. Your
            enquiry goes straight to {SITE.phoneDisplay} — usually confirmed
            within 30 minutes.
          </p>
          <div className="mt-8 space-y-3 text-sm">
            <div className="flex items-center gap-3 text-[var(--pam-text)]">
              <MessageCircle className="w-4 h-4 text-[var(--pam-gold)]" />
              WhatsApp: {SITE.phoneDisplay}
            </div>
            <div className="flex items-center gap-3 text-[var(--pam-text)]">
              <Phone className="w-4 h-4 text-[var(--pam-gold)]" />
              Call: {SITE.phoneDisplay}
            </div>
          </div>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          onSubmit={submit}
          className="md:col-span-7 bg-white border border-[var(--pam-forest)]/5 shadow-xl rounded-sm p-6 md:p-10"
          data-testid="booking-form"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <Field
              label="Full name"
              icon={<User className="w-4 h-4" />}
              testId="booking-name"
            >
              <input
                type="text"
                required
                value={form.name}
                onChange={update("name")}
                placeholder="Your name"
                className="w-full bg-transparent outline-none py-2 text-[var(--pam-text)]"
                data-testid="booking-input-name"
              />
            </Field>
            <Field
              label="Phone / WhatsApp"
              icon={<Phone className="w-4 h-4" />}
              testId="booking-phone"
            >
              <input
                type="tel"
                required
                value={form.phone}
                onChange={update("phone")}
                placeholder="+91 ..."
                className="w-full bg-transparent outline-none py-2 text-[var(--pam-text)]"
                data-testid="booking-input-phone"
              />
            </Field>
            <Field
              label="Check-in"
              icon={<CalendarDays className="w-4 h-4" />}
              testId="booking-checkin"
            >
              <input
                type="date"
                required
                value={form.checkIn}
                onChange={update("checkIn")}
                className="w-full bg-transparent outline-none py-2 text-[var(--pam-text)]"
                data-testid="booking-input-checkin"
              />
            </Field>
            <Field
              label="Check-out"
              icon={<CalendarDays className="w-4 h-4" />}
              testId="booking-checkout"
            >
              <input
                type="date"
                required
                value={form.checkOut}
                onChange={update("checkOut")}
                className="w-full bg-transparent outline-none py-2 text-[var(--pam-text)]"
                data-testid="booking-input-checkout"
              />
            </Field>
            <Field
              label="Guests"
              icon={<Users className="w-4 h-4" />}
              testId="booking-guests"
            >
              <input
                type="number"
                min="1"
                max="30"
                value={form.guests}
                onChange={update("guests")}
                className="w-full bg-transparent outline-none py-2 text-[var(--pam-text)]"
                data-testid="booking-input-guests"
              />
            </Field>
            <Field label="Room type" testId="booking-roomtype">
              <select
                value={form.roomType}
                onChange={update("roomType")}
                className="w-full bg-transparent outline-none py-2 text-[var(--pam-text)]"
                data-testid="booking-input-roomtype"
              >
                {ROOMS.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="mt-5">
            <Field label="Notes (optional)" testId="booking-notes">
              <textarea
                rows={3}
                value={form.notes}
                onChange={update("notes")}
                placeholder="Anniversary, dietary preferences, arrival time..."
                className="w-full bg-transparent outline-none py-2 text-[var(--pam-text)] resize-none"
                data-testid="booking-input-notes"
              />
            </Field>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <button
              type="submit"
              className="btn-gold rounded-full px-7 py-3.5 text-sm font-medium tracking-wide flex items-center gap-2"
              data-testid="booking-submit-btn"
            >
              <MessageCircle className="w-4 h-4" />
              Send on WhatsApp
            </button>
            <span className="text-xs text-[var(--pam-muted)]">
              We never share your details. No spam.
            </span>
          </div>
        </motion.form>
      </div>
    </section>
  );
}

function Field({ label, icon, children, testId }) {
  return (
    <label className="block" data-testid={testId}>
      <span className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[var(--pam-muted)]">
        {icon}
        {label}
      </span>
      <div className="mt-1 border-b border-[var(--pam-forest)]/20 focus-within:border-[var(--pam-gold)] transition-colors">
        {children}
      </div>
    </label>
  );
}
